import {
    fetchEnvironments,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,
    transferEnvironmentContent,
    fetchEnvironmentJob,
} from '../api/api';
import {
    PRODUCTION_SLUG,
    restoreActiveEnvironment,
    setActiveEnvironment,
} from '../utils/environment';

/**
 * Load a workspace's environments and settle on the active one.
 *
 * The stored selection is only honoured if it still exists — an environment
 * someone else deleted must not keep being sent on every request, which the
 * API would answer with a 400 for every page in the dashboard.
 */
export const getEnvironments = (workspaceId, withCounts = false) => async (dispatch) => {
    dispatch({ type: 'ENVIRONMENTS_REQUEST' });

    // Settle the stamping before the network call: requests fired while the
    // list is in flight still have to carry the right environment.
    const stored = restoreActiveEnvironment(workspaceId);

    // Show the remembered environment on the switcher straight away. Waiting
    // for the list would label a staging session "Production" for the first
    // moment after every page load, which is exactly the thing the switcher
    // exists to make unambiguous.
    dispatch({
        type: 'ENVIRONMENTS_RESTORED',
        payload: {
            workspaceId,
            active:
                stored && stored.slug !== PRODUCTION_SLUG
                    ? { _id: stored.id, slug: stored.slug, name: stored.name, isDefault: false }
                    : null,
        },
    });

    try {
        const res = await fetchEnvironments(workspaceId, withCounts);
        const environments = res?.data?.data ?? [];

        // The list only holds environments this member can access, so a member
        // with no access to Production gets their first remaining environment
        // rather than Production — falling back to null would mean Production.
        const match =
            environments.find((env) => env.slug === stored?.slug) ??
            environments.find((env) => env.isDefault) ??
            environments[0] ??
            null;
        const slugOf = (env) => (!env || env.isDefault ? PRODUCTION_SLUG : env.slug);

        setActiveEnvironment(workspaceId, match);

        dispatch({
            type: 'ENVIRONMENTS_SUCCESS',
            payload: { environments, workspaceId, active: match },
        });

        // The remembered environment is gone — someone else deleted it, or the
        // member lost access to it. The page has already fetched against it and
        // got nothing back, so start over in the environment we fell back to
        // rather than leave the user looking at an empty CMS.
        // setActiveEnvironment above already stored the fallback, so this
        // cannot loop.
        if (stored && slugOf(match) !== (stored.slug ?? PRODUCTION_SLUG)) {
            window.location.assign(`/${workspaceId}/content-models`);
        }

        return environments;
    } catch (error) {
        dispatch({ type: 'ENVIRONMENTS_FAIL', payload: error?.message });
        return [];
    }
};

export const switchEnvironment = (workspaceId, environment) => (dispatch) => {
    setActiveEnvironment(workspaceId, environment);
    dispatch({ type: 'ENVIRONMENT_SWITCHED', payload: environment });
};

export const clearEnvironments = () => (dispatch) => {
    dispatch({ type: 'ENVIRONMENTS_CLEARED' });
};

export const addEnvironment = (workspaceId, data) => async (dispatch) => {
    const res = await createEnvironment(workspaceId, data);
    if (res?.data?.status === 'success') {
        // The environment exists straight away; its seed (`jobId`) fills it in
        // on the queue, so the caller refreshes again once that finishes.
        await dispatch(getEnvironments(workspaceId, true));
    }
    return res?.data;
};

export const editEnvironment = (workspaceId, id, data) => async (dispatch) => {
    const res = await updateEnvironment(workspaceId, id, data);
    if (res?.data?.status === 'success') {
        await dispatch(getEnvironments(workspaceId, true));
    }
    return res?.data;
};

export const removeEnvironment = (workspaceId, id) => async (dispatch, getState) => {
    const res = await deleteEnvironment(workspaceId, id);
    if (res?.data?.status === 'success') {
        // Deleting the environment the user is standing in would leave every
        // request pointing at something that no longer exists.
        if (getState().environments?.active?._id === id) {
            setActiveEnvironment(workspaceId, { slug: PRODUCTION_SLUG, isDefault: true, name: 'Production' });
        }
        // The API hides it immediately and empties it on the queue, so the
        // refreshed list no longer contains it even though its content is
        // still being removed.
        await dispatch(getEnvironments(workspaceId, true));
    }
    return res?.data;
};

/**
 * Plan (dryRun) or start a copy/move between two environments.
 *
 * A dry run answers with `{ status: 'success', plan }`. Applying answers with
 * `{ status: 'accepted', jobId }` — pass that to `waitForEnvironmentJob`.
 */
export const transferContent = (workspaceId, payload) => async () => {
    const res = await transferEnvironmentContent(workspaceId, payload);
    return res?.data;
};

const JOB_POLL_INTERVAL_MS = 1500;
/**
 * A whole-workspace transfer is thousands of writes, so the ceiling is
 * generous. It exists to stop a modal polling forever against a worker that
 * died, not to bound the work.
 */
const JOB_POLL_TIMEOUT_MS = 30 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Follow a queued transfer, seed or purge to its end.
 *
 * Resolves with the terminal job record for BOTH outcomes — a failed transfer
 * is something the user has to be shown (with its reason), not an exception to
 * swallow. Only losing the job itself rejects.
 *
 * `isCancelled` lets a closing modal stop polling; it resolves with the last
 * state seen rather than leaving a request loop running behind a closed view.
 */
export const waitForEnvironmentJob = async (
    workspaceId,
    jobId,
    { onProgress, isCancelled } = {},
) => {
    const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
    let last = null;

    while (Date.now() < deadline) {
        if (isCancelled?.()) return last;

        // eslint-disable-next-line no-await-in-loop
        const res = await fetchEnvironmentJob(workspaceId, jobId);
        const job = res?.data?.data;
        if (!job) throw new Error('That transfer could not be found');

        last = job;
        onProgress?.(job);

        if (job.state === 'completed' || job.state === 'failed') return job;

        // eslint-disable-next-line no-await-in-loop
        await sleep(JOB_POLL_INTERVAL_MS);
    }

    throw new Error(
        'This is taking longer than expected. It is still running — reopen this screen to check on it.',
    );
};
